import { useAuth } from 'common/auth/auth-provider'
import ErrorBoundary from 'common/error-boundary'
import Backdrop from 'components/ui/backdrop'
import { Suspense, lazy, useEffect } from 'react'
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useLocation
} from 'react-router-dom'
import AnonymousRoute from './anonymous-route'
import BackofficeRoute from './backoffice-route'
import PrivateRoute from './private-route'
import { getRedirectRoute } from './redirect'
//import AccountRoute from './account-route'

// Componente para rastrear mudanças de página no Google Analytics
function GoogleAnalyticsTracker() {
  const location = useLocation()

  useEffect(() => {
    // Verifica se está em produção
    const isProduction = window.location.hostname === 'app.seulugar.imb.br'

    if (isProduction && window.gtag) {
      // Rastreia a visualização da página
      window.gtag('config', 'G-FZSX6MDGXJ', {
        page_path: location.pathname + location.search
      })
    }
  }, [location])

  return null
}

const AdminLayout = lazy(() => import('layouts/admin'))

const Login = lazy(() => import('pages/login'))
const ChangePassword = lazy(() => import('pages/change-password'))

const Dashboard = lazy(() => import('pages/admin/dashboard'))

const EmployeeDetail = lazy(() => import('pages/admin/employees/detail'))
const EmployeesList = lazy(() => import('pages/admin/employees/list'))

const UserSummary = lazy(() => import('pages/admin/users/summary'))
const UsersList = lazy(() => import('pages/admin/users/list'))
const UserCreate = lazy(() => import('pages/admin/users/new'))
const UserEdit = lazy(() => import('pages/admin/users/edit'))

const AccessList = lazy(() => import('pages/admin/access/list'))
const AccessDetail = lazy(() => import('pages/admin/access/detail'))
const AccessSummary = lazy(() => import('pages/admin/access/summary'))

const ContractDetail = lazy(() => import('pages/admin/contracts/detail'))
const ContractsList = lazy(() => import('pages/admin/contracts/list'))
const ContractPDF = lazy(() => import('pages/admin/contracts/pdf'))

const ProposalDetail = lazy(() => import('pages/admin/proposals/detail'))
const ProposalsList = lazy(() => import('pages/admin/proposals/list'))

const PropertyDetail = lazy(() => import('pages/admin/properties/detail'))
const PropertyOverview = lazy(() => import('pages/admin/properties/overview'))

const PropertiesList = lazy(() => import('pages/admin/properties/list'))
const CondominiumList = lazy(() => import('pages/admin/condominium/list'))
const CondominiumCreate = lazy(() => import('pages/admin/condominium/create'))
const CondominiumSee = lazy(() => import('pages/admin/condominium/see'))
const PropertiesSummary = lazy(() => import('pages/admin/properties/summary'))
const PropertiesNew = lazy(
  () => import('pages/admin/properties/detail/edit-property')
)

const LeadsGestao = lazy(() => import('pages/admin/leads/gestao'))
const LeadsHistorico = lazy(() => import('pages/admin/leads/historico'))

const MyAccount = lazy(() => import('pages/admin/my-account/detail'))

const Agency = lazy(() => import('pages/admin/agency'))

const SiteLayout = lazy(() => import('layouts/site'))

const Home = lazy(() => import('pages/site/home'))
const PropertyClientDetail = lazy(() => import('pages/site/property-detail'))
const Favorite = lazy(() => import('pages/site/favorites'))
const Announce = lazy(() => import('pages/site/announce'))
const KnowUs = lazy(() => import('pages/site/know-us'))
const Terms = lazy(() => import('pages/site/terms'))
const Privacy = lazy(() => import('pages/site/privacy'))
const Search = lazy(() => import('pages/site/search'))

function PropertiesNewRouteElement() {
  const navigate = useNavigate()
  return (
    <PropertiesNew onClose={() => navigate('/admin')} onSuccess={() => {}} />
  )
}

export default function Router() {
  const auth = useAuth()
  const isAuthenticated = auth.isAuthenticated()

  // Verifica e redireciona se necessário, mas só quando necessário
  useEffect(() => {
    // Só verifica se há dados de autenticação e se não estamos na rota de login
    if (auth.auth && window.location.pathname !== '/login') {
      auth.checkAndRedirectIfInvalid()
    }
  }, [auth.auth])

  const redirectRoute = getRedirectRoute(isAuthenticated)
  return (
    <ErrorBoundary>
      <Suspense fallback={<Backdrop />}>
        <BrowserRouter>
          <GoogleAnalyticsTracker />
          <Routes>
            {/*Admin Pages*/}
            <Route
              path={'/admin'}
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  redirectTo='/login'
                >
                  <AdminLayout />
                </PrivateRoute>
              }
            >
              <Route
                index
                element={auth.isEmployee ? <Dashboard /> : <MyAccount />}
              />
              <Route path='properties'>
                <Route path='' element={<PropertiesList />} />
                <Route path=':id' element={<PropertyDetail />} />
                <Route path='summary/:id' element={<PropertiesSummary />} />
                <Route path='overview/:id' element={<PropertyOverview />} />
                <Route
                  path='edit/:id'
                  element={<PropertiesNewRouteElement />}
                />
              </Route>
              <Route path='condominium'>
                <Route path='' element={<CondominiumList />} />
                <Route path=':id' element={<CondominiumCreate />} />
                <Route path='see/:id' element={<CondominiumSee />} />
              </Route>
              <Route path='leads'>
                <Route path='gestao' element={<LeadsGestao />} />
                <Route path='historico' element={<LeadsHistorico />} />
              </Route>

              {auth.isEmployee ? (
                <>
                  <Route path='proposals'>
                    <Route path='' element={<ProposalsList />} />
                    <Route path=':id' element={<ProposalDetail />} />
                  </Route>
                  <Route path='contracts'>
                    <Route path='' element={<ContractsList />} />
                    <Route path=':id' element={<ContractDetail />} />
                  </Route>
                  <Route path='users'>
                    <Route path='' element={<UsersList />} />
                    <Route path='new' element={<UserCreate />} />
                    <Route path='edit/:id' element={<UserEdit />} />
                    <Route path=':id' element={<UserSummary />} />
                  </Route>
                  <Route path='acessos'>
                    <Route path='' element={<AccessList />} />
                    <Route path='summary/:id' element={<AccessSummary />} />
                    <Route path=':id' element={<AccessDetail />} />
                  </Route>
                  <Route
                    path='employees'
                    element={
                      <PrivateRoute
                        isAuthenticated={auth.isAdmin}
                        redirectTo='/admin'
                      >
                        <Outlet />
                      </PrivateRoute>
                    }
                  >
                    <Route path='' element={<EmployeesList />} />
                    <Route path=':id' element={<EmployeeDetail />} />
                  </Route>
                  <Route path='agency'>
                    <Route path='' element={<Agency />} />
                  </Route>
                </>
              ) : null}
            </Route>
            {/*Contract Page*/}
            <Route
              path='/admin/contracts/:id/pdf'
              element={
                <PrivateRoute
                  isAuthenticated={isAuthenticated}
                  redirectTo='/login'
                >
                  <BackofficeRoute>
                    <ContractPDF />
                  </BackofficeRoute>
                </PrivateRoute>
              }
            />
            {/*Site Pages*/}
            <Route path='/' element={<SiteLayout />}>
              <Route index element={<Home />} />
              <Route path='favorites' element={<Favorite />} />
              <Route path='property/:id' element={<PropertyClientDetail />} />
              <Route path='announce' element={<Announce />} />
              <Route path='know-us' element={<KnowUs />} />
              <Route path='terms' element={<Terms />} />
              <Route path='privacy' element={<Privacy />} />
            </Route>

            <Route
              path='/'
              element={<SiteLayout hideFooter={true} hideHeader={true} />}
            >
              <Route
                path='login'
                element={
                  <AnonymousRoute
                    isAuthenticated={isAuthenticated}
                    redirectTo={redirectRoute}
                  >
                    <Login />
                  </AnonymousRoute>
                }
              />
              <Route
                path='change-password'
                element={
                  <AnonymousRoute
                    isAuthenticated={isAuthenticated}
                    redirectTo={redirectRoute}
                  >
                    <ChangePassword />
                  </AnonymousRoute>
                }
              />
            </Route>

            <Route path='/explore' element={<SiteLayout />}>
              <Route index element={<Search />} />
            </Route>

            <Route
              path='*'
              element={<Navigate to={isAuthenticated ? '/admin' : '/'} />}
            />
          </Routes>
        </BrowserRouter>
      </Suspense>
    </ErrorBoundary>
  )
}
